import { ApolloError } from '@apollo/client/core';
import { Action, createActionGroup, emptyProps, props } from '@ngrx/store';

import { CustomerEdit } from 'src/app/job/job-create/job-create-customer/job-create-customer.actions';

import {
    BulkEditCalendarEventMutation,
  DocumentReadyToViewSubscription,
  FullTransactionFragment,
  JobDetailsV2Fragment,
  UpdateJobMutation,
  UpdateJobMutationVariables,
} from '../../generated/graphql.generated';

import { ActionSource } from '../state/action-sources';

import { ErrorState, LoadingState } from '../utilities/state.util';

export interface JobsV2UpdatedLocation {
  id: string;
  locationType: string;
  addressLineOne: string;
}

export interface JobsV2LocationsChange {
  [locationType: string]: JobsV2UpdatedLocation;
}

export enum ActionInitiator {
  User,
  API,
}

export const JobToolActions = createActionGroup({
  source: ActionSource.JOB_TOOL,
  events: {
    'Tab Changed': props<{
      name: string;
      index: number;
    }>(),

    'Fetch Job Success': props<{
      job: JobDetailsV2Fragment;
      isCached: boolean;
    }>(),

    'Fetch Job Error': props<{
      jobId: string;
      error: Error;
      retry: boolean;
      removeFromQuickAccess?: boolean;
      searchedInZone?: string;
      skipNotify?: boolean;
    }>(),

    'Job Not Found Error': props<{
      jobId: string;
      didSetContext?: boolean;
    }>(),

    'Retry Fetch Job Triggered': props<{
      jobId: string;
    }>(),

    /**
     * Re-read the job in the BACKGROUND, from something the operator did not ask
     * for — a payment settling, a refund leg landing.
     *
     * ⚠ It is a separate trigger from `retryFetchJobTriggered` for one reason:
     * its FAILURE must be harmless. `fetchJobError` drives `redirectToJobPage`,
     * which navigates away from the job — correct when someone opened a job that
     * cannot be loaded, and completely wrong when a background poll blipped while
     * an operator has a customer at the terminal. So this trigger reports its own
     * failure and nothing routes off it.
     */
    'Background Refresh Triggered': props<{
      jobId: string;
    }>(),

    /** A background re-read failed. Deliberately inert: the next one will try again. */
    'Background Refresh Error': props<{
      jobId: string;
      error: Error;
    }>(),

    'Job Loading State Changed': props<{ jobId: string; loadingState: LoadingState | ErrorState }>(),
    'Save Changes Button Clicked': emptyProps(),
    'Discard Unsaved Changes Button Clicked': emptyProps(),
    'Build Save Changes Variables Error': props<{ error: Error }>(),
    'Build Save Changes Variables Success': props<{
      vars: UpdateJobMutationVariables;
      jobFranchiseId: string;
      changeIds: string[];
      promptedBy: Action;
      customerEdit?: CustomerEdit;
      /** New workorders can't carry metadata in addEvents — these are
       *  applied with a follow-up editCalendarEvents after the create. */
      pendingNewEventAttachments?: Array<{ eventId: string; eventAttachments: unknown[]; eventNotes?: string }>;
    }>(),
    'Save Changes Success': props<{
      updatedJob: JobDetailsV2Fragment;
      changeIds: string[];
      promptedBy: Action;
      locationsChanged: boolean;
      customerEdit?: CustomerEdit;
    }>(),
    'Save Changes Error': props<{ error: ApolloError; changeIds: string[] }>(),
    'Job Data Reset': props<{ job: JobDetailsV2Fragment }>(),
    'Update Job Area Success': props<{ updatedJob: JobDetailsV2Fragment }>(),
    /** Metadata-only job write (e.g. the Ops Summary). Deliberately NOT
     * handled by the whole-job-save reducers/effects: dispatching the full
     * Save Changes Success would reload QuickJS userInputs from metadata and
     * clear dirtyInputKeys, dropping unsaved calculator edits. */
    'Update Job Metadata Success': props<{ updatedJob: JobDetailsV2Fragment }>(),
    'Update Job Area Error': props<{ error: Error }>(),
    'Delete Job Success': props<{ jobId: string }>(),
    'Delete Job Error': props<{ error: Error }>(),

    'Job Loaded': props<{
      job: JobDetailsV2Fragment;
      source: 'cache' | 'server';
    }>(),
    'Document Ready To View': props<DocumentReadyToViewSubscription['documentReadyToView']>(),
    'Delete Transaction Success': props<{
      jobId: string,
      transactionId: string;
    }>(),
    'Delete Transaction Error': props<{
      error: Error,
    }>(),
    'Apply Transaction Success': props<{
      jobId: string;
      transaction: FullTransactionFragment;
    }>(),

    'Share Event Success': props<{
      eventId: string;
      jobId: string;
      zoneId: string | null;
    }>(),

    'Update Event Locations Success': props<{
      updatedEvent: BulkEditCalendarEventMutation['bulkEditCalendarEvent']['events'][number];
      jobId: string;
    }>(),
    'Update Event Locations Error': props<{
      error: Error;
    }>(),

    'Print Bol Requested': props<{
      eventId: string;
      jobId: string;
    }>(),

    'Print All Bols Requested': props<{
      jobId: string;
    }>(),

    'Import Job Button Clicked': emptyProps(),
    'Import Job To Current Zone Success': props<{ updatedJob: JobDetailsV2Fragment }>(),
    'Import Job To Current Zone Error': props<{ error: Error }>(),
    'Auto Assign Roles Success': props<{
      updatedJob: UpdateJobMutation['updateJobs']['jobs'][number];
      customSuccessMessage?: string;
    }>(),
    'Auto Assign Roles Error': props<{ error: Error }>(),
  },
});
